import { searchParamFromUrl } from '@components/hooks/ParamsProvider/searchParams'
import type { ToastProps } from '@components/primitives/Toasts/Toast'
import {
  DefaultToastMessages,
  Toast,
} from '@components/primitives/Toasts/Toast'
import upsert from '@lib/api/shortcode/upsert'
import type { UTMParameters } from '@lib/browsers/utm/types'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import ExternalRoutes from '@lib/routes/ExternalRoutes'
import PatientRoutes from '@lib/routes/PatientRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import { analytics, trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { type TrackingSources } from '@lib/tracking/TrackingSources'
import getUserIdTokenFromJwtCookie from '@modules/auth/jwt/getUserIdTokenFromJwtCookie'
import { shareTrialUtmParams } from '@modules/trials/helpers/shareTrialUtmParams'
import type { UtmLookup } from '@prisma/client'
import { useState } from 'react'
import CopyLinkSvg from './CopyLinkSvg'
import EmailSvg from './EmailSvg'
import FacebookSvg from './FacebookSvg'
import LinkedInSvg from './LinkedInSvg'
import MessengerSvg from './MessengerSvg'
import RedditSvg from './RedditSvg'
import { ShareTrialViaButton } from './ShareTrialViaButton'
import TwitterSvg from './TwitterSvg'

export type ShareTrialButtonsGridProps = {
  additionalMessage?: string
  shareTrialCodes: UtmLookup[]
  showCopyLink?: boolean
  source:
    | typeof TrackingSources.LISTING
    | typeof TrackingSources.PATIENT_APPLY_PACKAGE
    | typeof TrackingSources.PATIENT_POST_APPLY
    | typeof TrackingSources.TRIAL_APPLICATION_FORM
  trialSlug: string
  trialTitle: string
}

// This is public info, maybe extract this out to a constant if we need it elsewhere
const facebookAppId = '1187327332246073'

export default function ShareTrialButtonsGrid({
  additionalMessage,
  shareTrialCodes,
  showCopyLink = true,
  source,
  trialSlug,
  trialTitle,
}: ShareTrialButtonsGridProps) {
  const [toastSettings, showToast] = useState<ToastProps | null>(null)
  const copyLinkClick = async () => {
    navigator.clipboard.writeText(
      await getShareTrialUrlForChannel({
        channel: 'link',
        trialSlug,
        utmLookups: shareTrialCodes,
      }),
    )
    showToast({ ...DefaultToastMessages.copy, className: 'w-32 rounded-xl' })
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_COPY_LINK_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const emailClick = async () => {
    const shareTrialUrl = await getShareTrialUrlForChannel({
      channel: 'email',
      trialSlug,
      utmLookups: shareTrialCodes,
    })
    const shareTrialPreface = getShareTrialPreface({
      additionalMessage,
      shareTrialUrl,
      trialTitle,
    })
    window.open(
      `mailto:?subject=Check out this study: ${trialTitle}&body=${shareTrialPreface}`,
      '_blank',
      'noopener,noreferrer',
    )
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_EMAIL_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const facebookClick = async () => {
    const shareTrialUrl = encodeURI(
      await getShareTrialUrlForChannel({
        channel: 'facebook',
        trialSlug,
        utmLookups: shareTrialCodes,
      }),
    )
    const returnToTrialUrl = encodeURI(
      urlWithQueryParams(window.location.href, { share: 'success' }).toString(),
    )
    const facebookShareUrl = urlWithQueryParams(ExternalRoutes.facebookShare, {
      app_id: facebookAppId,
      display: 'page',
      href: shareTrialUrl,
      redirect_uri: returnToTrialUrl,
    }).toString()
    window.open(facebookShareUrl, '_blank', 'noopener,noreferrer')
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_FACEBOOK_SHARE_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const redditClick = async () => {
    const shareTrialUrl = encodeURI(
      await getShareTrialUrlForChannel({
        channel: 'reddit',
        trialSlug,
        utmLookups: shareTrialCodes,
      }),
    )

    const redditRoute = ExternalRoutes.subRedditShare('clinicalresearch')

    const redditShareUrl = urlWithQueryParams(redditRoute, {
      url: shareTrialUrl,
    }).toString()
    window.open(redditShareUrl, '_blank', 'noopener,noreferrer')
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_REDDIT_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const twitterClick = async () => {
    const shareTrialUrl = await getShareTrialUrlForChannel({
      channel: 'twitter',
      trialSlug,
      utmLookups: shareTrialCodes,
    })
    const shareTrialTextContent = getShareTrialPreface({
      shareTrialUrl,
      trialTitle,
    })

    const twitterShareUrl = `${ExternalRoutes.twitterShare}/?text=${shareTrialTextContent}`
    window.open(twitterShareUrl, '_blank', 'noopener,noreferrer')
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_TWITTER_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const messengerClick = () => {
    const utmLookup = shareTrialCodes.find(
      ({ utmMedium }) => utmMedium?.toLowerCase() === 'messenger',
    )
    const shareTrialUrl = encodeURI(
      absoluteUrl(
        urlWithQueryParams(DynamicRoutes.trial(trialSlug).toString(), {
          utm_campaign:
            utmLookup?.utmCampaign ?? shareTrialUtmParams.utmCampaign,
          utm_medium: 'messenger',
          utm_source: utmLookup?.utmSource ?? shareTrialUtmParams.utmSource,
        } as UTMParameters), // Casting to UTMParameters to satisfy the type checker, but these might not be in our allow-list as they come from the database
        { forceProduction: true },
      ),
    )
    const returnToTrialUrl = encodeURI(
      urlWithQueryParams(window.location.href, { share: 'success' }).toString(),
    )
    const facebookShareUrl = urlWithQueryParams(
      ExternalRoutes.facebookMessengerShare,
      {
        app_id: facebookAppId,
        link: shareTrialUrl,
        redirect_uri: returnToTrialUrl,
      },
    ).toString()
    window.open(facebookShareUrl, '_blank', 'noopener,noreferrer')
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_FACEBOOK_MESSENGER_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  const linkedinClick = async () => {
    const shareTrialUrl = encodeURI(
      await getShareTrialUrlForChannel({
        channel: 'linkedin',
        trialSlug,
        utmLookups: shareTrialCodes,
      }),
    )
    const redditShareUrl = urlWithQueryParams(ExternalRoutes.linkedInShare, {
      url: shareTrialUrl,
    }).toString()
    window.open(redditShareUrl, '_blank', 'noopener,noreferrer')
    trackEvent(TrackingEvents.SHARE_TRIAL_VIA_LINKED_IN_CLICKED, {
      source,
      val: trialSlug,
    })
  }

  return (
    <div className='grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2'>
      {showCopyLink && (
        <ShareTrialViaButton label='Copy Link' onClick={copyLinkClick}>
          <CopyLinkSvg />
        </ShareTrialViaButton>
      )}
      <ShareTrialViaButton label='Email' onClick={emailClick}>
        <EmailSvg />
      </ShareTrialViaButton>
      <ShareTrialViaButton label='Facebook' onClick={facebookClick}>
        <FacebookSvg />
      </ShareTrialViaButton>
      <ShareTrialViaButton label='Reddit' onClick={redditClick}>
        <RedditSvg />
      </ShareTrialViaButton>
      <ShareTrialViaButton label='Twitter' onClick={twitterClick}>
        <TwitterSvg />
      </ShareTrialViaButton>
      <ShareTrialViaButton label='Messenger' onClick={messengerClick}>
        <MessengerSvg />
      </ShareTrialViaButton>
      <ShareTrialViaButton label='LinkedIn' onClick={linkedinClick}>
        <LinkedInSvg />
      </ShareTrialViaButton>
      <Toast showToast={showToast} toastSettings={toastSettings} />
    </div>
  )
}

async function getShareTrialUrlForChannel({
  channel,
  trialSlug,
  utmLookups,
}: {
  channel: string
  trialSlug: string
  utmLookups: UtmLookup[]
}) {
  const userIdToken = getUserIdTokenFromJwtCookie()
  const createdBy = userIdToken ?? analytics.user().anonymousId
  const hasShareModal = searchParamFromUrl('shareModal') === 'true'

  const utmLookup = getUtmLookup(utmLookups, hasShareModal, channel)
  const utmParams = {
    utm_campaign: utmLookup?.utmCampaign ?? '',
    utm_content: utmLookup?.utmContent ?? '',
    utm_medium: channel ?? utmLookup?.utmMedium,
    utm_source: utmLookup?.utmSource ?? '',
    utm_term: utmLookup?.utmTerm ?? '',
  } as UTMParameters // Casting to UTMParameters to satisfy the type checker, but these might not be in our allow-list as they come from the database

  const url = urlWithQueryParams(
    absoluteUrl(PatientRoutes.patientReferral(trialSlug)),
    {
      invitedBy: userIdToken ?? '',
      ...utmParams,
    },
  ).toString()

  const result = await upsert({
    createdBy: userIdToken,
    url: url,
  })
  if (result.success && result.data) {
    return urlWithQueryParams(absoluteUrl(DynamicRoutes.go(result.data)), {
      createdBy,
    }).toString()
  }

  return absoluteUrl(PatientRoutes.patientReferral(trialSlug), {
    forceProduction: true,
  })
}

function getShareTrialPreface({
  additionalMessage,
  shareTrialUrl,
  trialTitle,
}: {
  additionalMessage?: string
  shareTrialUrl: string
  trialTitle: string
}) {
  if (additionalMessage) {
    return `I just found a new study: ${trialTitle}! Check it out at ${shareTrialUrl}. ${additionalMessage}`
  } else {
    return `I just found a new study: ${trialTitle}! Check it out at ${shareTrialUrl}`
  }
}

function getUtmLookup(
  utmLookups: UtmLookup[],
  hasShareModal: boolean,
  channel: string,
) {
  const utmLookup = utmLookups.find(
    ({ utmMedium }) => utmMedium?.toLowerCase() === channel,
  )

  if (!utmLookup) {
    return
  }

  if (!hasShareModal) {
    return utmLookup
  }

  return {
    ...utmLookup,
    utmContent: 'share_icon_same_condition_recommended_trial_1',
    utmSource: 'recommendations_email',
  }
}
